import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Skeleton,
} from "@mui/material";
import {
  Event,
  Assignment,
  CheckCircle,
  Pending,
  AddCircle,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  TaskAlt,
} from "@mui/icons-material";
import { formatDateShort } from "../../utils/dateUtils";
import api from "../../services/api";

// ─── Mapa de colores por estado de evento ────────────────────────────────────
const ESTADO_EVENTO_COLORS = {
  Presupuesto:     "#9E9E9E",
  Pendiente:       "#FFC107",
  "Por Confirmar": "#FF9800",
  Confirmado:      "#2196F3",
  "En proceso":    "#9C27B0",
  Completado:      "#4CAF50",
  "Día Laborado":  "#4CAF50",
  Cancelado:       "#F44336",
};

// ─── Mapa de colores por prioridad de tarea ───────────────────────────────────
const PRIORIDAD_COLORS = {
  Alta:  "#F44336",
  Media: "#FFC107",
  Baja:  "#4CAF50",
};

// ─── Tarjeta KPI circular ──────────────────────────────────────────────────────
const StatCard = ({ title, value, icon, color, loading }) => (
  <Box
    sx={{
      width: { xs: 110, sm: 145 },
      height: { xs: 110, sm: 145 },
      borderRadius: "50%",
      background: `radial-gradient(circle at 35% 35%, ${color}40, ${color}15)`,
      border: `2px solid ${color}50`,
      boxShadow: `0 0 24px ${color}30`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: { xs: 0.3, sm: 0.5 },
      mx: "auto",
      transition: "transform .2s, box-shadow .2s",
      "&:hover": { transform: "scale(1.06)", boxShadow: `0 0 32px ${color}50` },
    }}
  >
    <Box sx={{ color, lineHeight: 1, "& .MuiSvgIcon-root": { fontSize: { xs: 24, sm: 28 } } }}>
      {icon}
    </Box>
    {loading ? (
      <Skeleton variant="text" width={36} height={30} sx={{ bgcolor: "rgba(255,255,255,0.1)", borderRadius: 1 }} />
    ) : (
      <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.15rem", sm: "1.4rem" }, color: "#fff", lineHeight: 1 }}>
        {value}
      </Typography>
    )}
    <Typography sx={{ fontSize: { xs: "0.52rem", sm: "0.62rem" }, color: "rgba(255,255,255,0.72)", fontWeight: 600, textAlign: "center", lineHeight: 1.2, px: 1 }}>
      {title}
    </Typography>
  </Box>
);

// ─── Componente principal ─────────────────────────────────────────────────────
const ColaboradorDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/colaborador/dashboard");
      setData(res.data);
    } catch (err) {
      if (err.response?.status !== 401) {
        setError("No se pudieron cargar los datos del dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const stats             = data?.stats ?? {};
  const proximosEventos   = data?.proximos_eventos ?? [];
  const tareasPendientes  = data?.tareas_pendientes ?? [];

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
        Mi Dashboard
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* ── Círculos de estadísticas — contenedor independiente, siempre arriba ── */}
      <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center" sx={{ mb: 3 }}>
        {[
          { title: "Próximos Eventos",  value: stats.eventos_proximos  ?? 0, icon: <Assignment />, color: "#FF5252" },
          { title: "Eventos Asignados", value: stats.eventos_asignados ?? 0, icon: <Event />,       color: "#00C9FF" },
          { title: "Eventos Generados", value: stats.eventos_generados ?? 0, icon: <AddCircle />,   color: "#C77DFF" },
          { title: "Tareas Pendientes",  value: stats.tareas_pendientes  ?? 0, icon: <Pending />,     color: "#FFC107" },
          { title: "Tareas Completadas", value: stats.tareas_completadas ?? 0, icon: <CheckCircle />, color: "#92FE9D" },
        ].map((stat) => (
          <Grid item xs={4} sm="auto" key={stat.title} sx={{ display: "flex", justifyContent: "center" }}>
            <StatCard {...stat} loading={loading} />
          </Grid>
        ))}
      </Grid>

      {/* ── Tarjetas de lista — contenedor independiente, siempre debajo de los círculos ── */}
      <Grid container rowSpacing={{ xs: 2, sm: 3 }} columnSpacing={{ xs: 0, sm: 3 }}>
        {/* ── Lista Próximos Eventos ── */}
        <Grid item xs={12} md={7}>
          <Paper
            sx={{
              p: 3,
              background: "rgba(13,17,30,0.97)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 3,
              height: "100%",
              maxHeight: { xs: 340, sm: "none" },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexShrink: 0 }}>
              <CalendarIcon sx={{ color: "#00C9FF", fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Próximos Eventos
              </Typography>
            </Box>
            <Box sx={{
              flex: 1,
              overflow: "auto",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: 3 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.15)", borderRadius: 2 },
            }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : proximosEventos.length === 0 ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No tienes eventos próximos asignados
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {proximosEventos.map((evento, index) => {
                  const estadoColor = ESTADO_EVENTO_COLORS[evento.estado] || "#9E9E9E";
                  return (
                    <React.Fragment key={evento.id}>
                      <ListItem
                        sx={{
                          borderRadius: 2,
                          px: 1.5,
                          py: 1,
                          "&:hover": { background: "rgba(0,201,255,0.08)" },
                        }}
                      >
                        <Box
                          sx={{
                            width: 4,
                            height: 44,
                            borderRadius: 2,
                            bgcolor: estadoColor,
                            mr: 1.5,
                            flexShrink: 0,
                          }}
                        />
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {evento.titulo}
                              </Typography>
                              <Chip
                                label={evento.estado}
                                size="small"
                                sx={{
                                  bgcolor: `${estadoColor}25`,
                                  color: estadoColor,
                                  fontWeight: 600,
                                  fontSize: 11,
                                  height: 20,
                                }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.25, display: "flex", gap: 2, flexWrap: "wrap" }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <CalendarIcon sx={{ fontSize: 13, color: "text.disabled" }} />
                                <Typography variant="caption" color="text.secondary">
                                  {evento.fecha_evento
                                    ? formatDateShort(evento.fecha_evento)
                                    : "—"}
                                  {evento.hora_inicio ? ` · ${evento.hora_inicio}` : ""}
                                </Typography>
                              </Box>
                              {evento.ubicacion && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <LocationIcon sx={{ fontSize: 13, color: "text.disabled" }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {evento.ubicacion}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < proximosEventos.length - 1 && (
                        <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mx: 2 }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </List>
            )}
            </Box>
          </Paper>
        </Grid>

        {/* ── Lista Tareas Pendientes ── */}
        <Grid item xs={12} md={5}>
          <Paper
            sx={{
              p: 3,
              background: "rgba(13,17,30,0.97)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 3,
              height: "100%",
              maxHeight: { xs: 340, sm: "none" },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexShrink: 0 }}>
              <TaskAlt sx={{ color: "#92FE9D", fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Tareas Pendientes
              </Typography>
              {!loading && (stats.tareas_pendientes ?? 0) > 0 && (
                <Chip
                  label={stats.tareas_pendientes}
                  size="small"
                  sx={{
                    bgcolor: "#FFC10725",
                    color: "#FFC107",
                    fontWeight: 700,
                    fontSize: 11,
                    ml: "auto",
                  }}
                />
              )}
            </Box>
            <Box sx={{
              flex: 1,
              overflow: "auto",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: 3 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.15)", borderRadius: 2 },
            }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : tareasPendientes.length === 0 ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No tienes tareas pendientes
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {tareasPendientes.map((tarea, index) => {
                  const prioColor = PRIORIDAD_COLORS[tarea.prioridad] || "#9E9E9E";
                  return (
                    <React.Fragment key={tarea.id}>
                      <ListItem
                        sx={{
                          borderRadius: 2,
                          px: 1.5,
                          py: 1,
                          "&:hover": { background: "rgba(146,254,157,0.08)" },
                        }}
                      >
                        <Box
                          sx={{
                            width: 4,
                            height: 36,
                            borderRadius: 2,
                            bgcolor: prioColor,
                            mr: 1.5,
                            flexShrink: 0,
                          }}
                        />
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {tarea.titulo}
                              </Typography>
                              {tarea.prioridad && (
                                <Chip
                                  label={tarea.prioridad}
                                  size="small"
                                  sx={{
                                    bgcolor: `${prioColor}25`,
                                    color: prioColor,
                                    fontWeight: 600,
                                    fontSize: 10,
                                    height: 18,
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            tarea.evento_titulo ? (
                              <Typography variant="caption" color="text.secondary">
                                📌 {tarea.evento_titulo}
                              </Typography>
                            ) : null
                          }
                        />
                        {tarea.estado === "En proceso" && (
                          <Chip
                            label="En proceso"
                            size="small"
                            sx={{ bgcolor: "#9C27B025", color: "#9C27B0", fontSize: 10, height: 18 }}
                          />
                        )}
                      </ListItem>
                      {index < tareasPendientes.length - 1 && (
                        <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mx: 2 }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </List>
            )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ColaboradorDashboard;
